import { useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@cieda/djayma-ui";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { MessageSquare, Send } from "lucide-react";
import type { OrderNote } from "@/lib/admin-api/orders";

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface OrderNotesProps {
  readonly notes: readonly OrderNote[];
  readonly isSubmitting: boolean;
  readonly onAddNote: (text: string) => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function OrderNotes({ notes, isSubmitting, onAddNote }: OrderNotesProps) {
  const { t } = useTranslation("admin");
  const [text, setText] = useState("");

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      const trimmed = text.trim();
      if (trimmed.length === 0) return;
      onAddNote(trimmed);
      setText("");
    },
    [text, onAddNote],
  );

  return (
    <div className="rounded-xl border border-stone-200 bg-white">
      <div className="border-b border-stone-100 px-5 py-4">
        <h3 className="font-display text-base font-semibold text-stone-900">
          {t("order_detail.notes")}
        </h3>
      </div>

      {/* Note list */}
      <div className="max-h-80 divide-y divide-stone-100 overflow-y-auto">
        {notes.length === 0 ? (
          <div className="px-5 py-8 text-center">
            <MessageSquare className="mx-auto h-6 w-6 text-stone-300" />
            <p className="mt-2 text-sm text-stone-500">
              {t("order_detail.note_empty")}
            </p>
          </div>
        ) : (
          notes.map((note) => (
            <div key={note.id} className="px-5 py-3">
              <div className="flex items-baseline justify-between gap-2">
                <span className="text-sm font-medium text-stone-900">
                  {note.admin_user?.full_name ?? "Admin"}
                </span>
                <span className="shrink-0 text-xs text-stone-400">
                  {format(new Date(note.created_at), "dd/MM/yyyy HH:mm", {
                    locale: fr,
                  })}
                </span>
              </div>
              <p className="mt-1 text-sm text-stone-700 whitespace-pre-wrap">
                {note.text}
              </p>
            </div>
          ))
        )}
      </div>

      {/* Add note form */}
      <form
        onSubmit={handleSubmit}
        className="flex items-end gap-2 border-t border-stone-100 p-4"
      >
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={t("order_detail.note_placeholder")}
          rows={2}
          className="flex-1 resize-none rounded-lg border border-stone-200 bg-stone-50 px-3 py-2 text-sm text-stone-900 placeholder:text-stone-400 focus:border-amber-400 focus:outline-none focus:ring-1 focus:ring-amber-400"
        />
        <Button
          type="submit"
          disabled={isSubmitting || text.trim().length === 0}
          className="shrink-0"
        >
          <Send className="h-4 w-4" />
          <span className="ml-1.5 hidden sm:inline">
            {t("order_detail.note_submit")}
          </span>
        </Button>
      </form>
    </div>
  );
}
